import { useEffect, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetCertificationProgressQuery,
  useGetKnowledgeCheckQuery,
} from "@/redux/service";
import type { AttemptState } from "@/redux/certifications/types";
import {
  getKnowledgeCheckState,
  getLatestAttempt,
} from "@/domains/certifications/knowledgeCheck";
import { UseKnowledgeCheckOptions } from "@/domains/certifications/types";
import { ATTEMPT_STATES } from "@/domains/certifications/constants";

const GRADING_POLL_MS = 5000;
const MAX_GRADING_POLLS = 24;
const GRADING_STATES: AttemptState[] = [
  ATTEMPT_STATES.SUBMITTED,
  ATTEMPT_STATES.AI_REVIEWING,
];

export const useKnowledgeCheck = ({
  certificationSlug,
  knowledgeCheckId,
}: UseKnowledgeCheckOptions) => {
  const { refetch: refetchProgress } =
    useGetCertificationProgressQuery(certificationSlug);
  const [isGrading, setIsGrading] = useState(false);
  const [polls, setPolls] = useState(0);

  const query = useGetKnowledgeCheckQuery(knowledgeCheckId ?? skipToken, {
    pollingInterval:
      isGrading && polls < MAX_GRADING_POLLS ? GRADING_POLL_MS : 0,
  });
  const { data: check, startedTimeStamp } = query;
  const latestState = check
    ? (getLatestAttempt(check)?.state ?? null)
    : undefined;
  const previousStateRef = useRef<AttemptState | null | undefined>(undefined);

  useEffect(() => {
    const grading = Boolean(
      latestState && GRADING_STATES.includes(latestState),
    );
    setIsGrading(grading);
    setPolls((count) => (grading ? count + 1 : 0));
  }, [latestState, startedTimeStamp]);

  useEffect(() => {
    if (latestState === undefined) {
      return;
    }
    const previous = previousStateRef.current;
    previousStateRef.current = latestState;
    const isDecision =
      latestState !== null && !GRADING_STATES.includes(latestState);
    if (previous !== undefined && previous !== latestState && isDecision) {
      void refetchProgress();
    }
  }, [latestState, refetchProgress]);

  return {
    ...query,
    state: check ? getKnowledgeCheckState(check) : null,
  };
};
